import { COPY, humanDate, pct1 } from "./format";

/** PP-01.3: every analysis output can include or exclude ad spend. The URL key is `pp_ads`; absent means Included. */
export type AdsMode = "included" | "excluded";
export function adsFromSearch(value: unknown): AdsMode { return value === "excluded" ? "excluded" : "included"; }

export const CONTRIBUTION_COPY = {
  header: (ads: AdsMode) => (ads === "excluded" ? "Contribution (ex ads)" : "Contribution"),
  /** DEC-1413 A: in an order-type view the column shows the grid's single-order figure, and the header says so. */
  headerInView: (ads: AdsMode) => `${ads === "excluded" ? "Contribution (ex ads)" : "Contribution"} (single order)`,
  notServed: "not served by this grid yet",
  notComputed: "not computed",
  adsToggle: "Ads",
  included: "Included",
  excluded: "Excluded",
  title: "TRUE Contribution Margin after average discount, refunds and ad spend",
  titleExAds: "TRUE Contribution Margin after average discount and refunds, without ad spend",
  detailLine: (withoutAds: string) => `TRUE Contribution Margin after average discount, refunds and ad spend · without ads ${withoutAds}`,
  detailLineExAds: (withAds: string) => `TRUE Contribution Margin after average discount and refunds, without ad spend · with ads ${withAds}`,
} as const;

const ITEM_LABEL: Record<string, string> = {
  cogs_fulfilment: "no COGS or fulfilment cost",
  avg_discount: "no discount feed",
  avg_refund: "no refund feed",
  avg_ads: "no ads feed",
  no_orders: "no orders to weight the store figure",
  no_fulfilment_rows: "no fulfilment cost rows",
  not_loaded: "inputs not loaded",
};
/** Feed names inside a suffixed code ("avg_ads_no_fx_rate"), as a reader says them. */
const FEED_NAME: Record<string, string> = { avg_discount: "discount", avg_refund: "refund", avg_ads: "ads" };
/** Every reason the TRUE CM feed (precision-pricing feeds/tcm.py) and the grid (grid/compute.py) write after
 * "<input>:", in plain English. "unreadable_*" carry an order number and match by prefix. */
const DETAIL_LABEL: Record<string, string> = {
  no_google_ads_account: "no Google Ads account",
  google_ads_no_rows: "no Google Ads spend rows",
  google_ads_no_amount_or_currency: "Google Ads rows without an amount or currency",
  google_ads_stale: "Google Ads stale",
  spend_without_revenue: "ad spend but no order revenue to share it over",
  stale: "stale",
};
const DETAIL_PREFIX: [RegExp, (n: string) => string][] = [
  [/^unreadable_line order (\S+)$/, (n) => `unreadable order line, order ${n}`],
  [/^unreadable_discount order (\S+)$/, (n) => `unreadable discount, order ${n}`],
];
/** Said when the grid names a code this portal does not know yet: never the raw code. */
export const UNKNOWN_REASON = "an input is missing";

function detailText(detail: string): string {
  // a date reads with its year ("since 16 Sept 2026"), so a prior-year date never looks recent
  const dated = /^(.*?)\s*since (\d{4}-\d{2}-\d{2})$/.exec(detail);
  const head = dated ? dated[1].trim() : detail;
  const since = dated ? `since ${humanDate(dated[2])}` : "";
  const prefixed = DETAIL_PREFIX.find(([re]) => re.test(head));
  const known = prefixed ? prefixed[1](head.match(prefixed[0])![1]) : DETAIL_LABEL[head];
  // an unknown head with a date still says the feed is stale
  const words = known ?? (dated ? "stale" : UNKNOWN_REASON);
  return [words, since].filter(Boolean).join(" ");
}
/** One missing-list entry in plain English: "avg_ads: no_google_ads_account" reads "no ads feed (no Google Ads account)".
 * A code this map does not know reads UNKNOWN_REASON, never the raw code. */
export function missingReason(item: string): string {
  const at = item.indexOf(":");
  const key = (at < 0 ? item : item.slice(0, at)).trim();
  const detail = at < 0 ? "" : item.slice(at + 1).trim();
  const suffixed = /^(avg_discount|avg_refund|avg_ads)_(no_fx_rate|no_vat_rate)$/.exec(key);
  if (suffixed) return `no ${suffixed[2] === "no_fx_rate" ? "exchange rate" : "VAT rate"} for the ${FEED_NAME[suffixed[1]]} cost`;
  const label = ITEM_LABEL[key];
  if (!label) return UNKNOWN_REASON;
  if (!detail) return label;
  return `${label} (${detailText(detail)})`;
}

export type ContributionCell =
  | { kind: "value"; text: string; negative: boolean }
  | { kind: "missing"; label: string; reason: string };

/** The fields the Contribution cell reads: a grid row's `Margins` or a projection's `ProjectedMargins`. */
export type ContributionMargins = {
  true_contribution_pct?: string | null; true_contribution_missing?: string[];
  true_contribution_ex_ads_pct?: string | null; true_contribution_ex_ads_missing?: string[];
};

/** What the Contribution column shows: the grid's figure as served (a string, never computed here), or Missing and why. */
export function contributionCell(margins: ContributionMargins, ads: AdsMode): ContributionCell {
  const excluded = ads === "excluded";
  const pct = excluded ? margins.true_contribution_ex_ads_pct : margins.true_contribution_pct;
  const list = excluded ? margins.true_contribution_ex_ads_missing : margins.true_contribution_missing;
  if (pct != null) return { kind: "value", text: pct1(pct), negative: pct.startsWith("-") && !/^-0(\.0+)?$/.test(pct) };
  if (excluded && pct === undefined && list === undefined) return { kind: "missing", label: COPY.missing, reason: CONTRIBUTION_COPY.notServed };
  const reasons = [...new Set((list ?? []).map(missingReason))];
  return { kind: "missing", label: COPY.missing, reason: reasons.length ? reasons.join("; ") : CONTRIBUTION_COPY.notComputed };
}

/** The drawer's one-line explanation under the tiles. */
export function contributionLine(margins: ContributionMargins, ads: AdsMode): string {
  const cell = contributionCell(margins, ads);
  const other = contributionCell(margins, ads === "included" ? "excluded" : "included");
  if (cell.kind === "missing") {
    const line = `${ads === "excluded" ? CONTRIBUTION_COPY.titleExAds : CONTRIBUTION_COPY.title}: ${cell.reason}`;
    // the other mode's served figure still shows, as on the computed path
    return other.kind === "value" ? `${line} · ${ads === "included" ? "without" : "with"} ads ${other.text}` : line;
  }
  const otherText = other.kind === "value" ? other.text : other.label;
  return ads === "included" ? CONTRIBUTION_COPY.detailLine(otherText) : CONTRIBUTION_COPY.detailLineExAds(otherText);
}
